// src/hooks/useVersionCheck.js
import { useState, useEffect, useCallback, useRef } from 'react';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutos
const VERSION_URL = '/version.json';
const VERSION_CONFIG_URL = '/version-config';

/**
 * Compara duas versões semânticas (ex: "0.1.2" vs "0.1.1")
 * Retorna: -1 se a < b, 0 se a === b, 1 se a > b
 */
function compareVersions(a, b) {
  if (!a || !b) return 0;
  const partsA = String(a).split('.').map(Number);
  const partsB = String(b).split('.').map(Number);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const numA = partsA[i] || 0;
    const numB = partsB[i] || 0;
    if (numA < numB) return -1;
    if (numA > numB) return 1;
  }
  return 0;
}

/** Retorna a versão mais alta entre duas strings semver, ou a que existir. */
function maxVersion(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;
  return compareVersions(a, b) >= 0 ? a : b;
}

const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache',
  'Pragma': 'no-cache',
};

/**
 * Detecta modo de teste E2E. Considera tanto `?e2e=1` na URL quanto a flag
 * global `window.__E2E__` — esta última é injetada por addInitScript do
 * Playwright e também faz o index.html definir APP_VERSION="e2e". Sem cobrir
 * os dois casos, specs que setam apenas `__E2E__` (sem `?e2e=1` na navegação)
 * disparavam o ForceUpdateModal no meio do funil (APP_VERSION "e2e" < minVersion).
 */
function isE2E() {
  try {
    if (typeof window === 'undefined') return false;
    if (window.__E2E__ === true) return true;
    if (/\be2e=1\b/.test(window.location.search || '')) return true;
    // Execuções sintéticas rodando CONTRA PRODUÇÃO (test:e2e:prod): specs que
    // exercitam a máquina de estados real usam __SKIP_CACHE_BUST__ (sem
    // __E2E__), e o index.html gera APP_VERSION 'test-NNNNNN' — que nunca
    // converge para a minVersion do servidor. Sem este guard, o VersionCheck
    // dispara auto-reload a cada ciclo e derruba qualquer spec com mais de
    // ~12s (detectado 11/08/2026 no gate enforcement da triagem). Mesma
    // heurística de isSyntheticRun() em telemetry/queue.js; navegadores reais
    // não têm navigator.webdriver nem APP_VERSION sintética.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return true;
    if (window.__SKIP_CACHE_BUST__ === true) return true;
    const v = String(window.APP_VERSION || '');
    return v === 'e2e' || v.startsWith('test-');
  } catch {
    return false;
  }
}

/**
 * Evita interromper pacientes em consulta ativa com o modal bloqueante.
 * Se houver canal aberto e não finalizado, rebaixamos "forceUpdate" para
 * "updateAvailable" (banner suave) — o paciente continua o atendimento e
 * pode atualizar quando terminar (ou quando der F5).
 */
function hasActiveChat() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const channel = window.localStorage.getItem('activeChannelId');
    const finalized = window.localStorage.getItem('finalizedAt');
    return !!channel && !finalized;
  } catch {
    return false;
  }
}

/**
 * Busca /version.json (estático, rápido) e /version-config (Firestore, dinâmico).
 * Faz merge dos dois usando o critério mais restritivo:
 *   - forceUpdate: true se qualquer um dos dois for true
 *   - minVersion: a maior entre os dois
 */
async function fetchVersionData(now) {
  const opts = { method: 'GET', cache: 'no-store', headers: NO_CACHE_HEADERS };

  // Busca ambos em paralelo; version.json é obrigatório, version-config é fallback
  const [staticRes, configRes] = await Promise.allSettled([
    fetch(`${VERSION_URL}?t=${now}`, opts),
    fetch(`${VERSION_CONFIG_URL}?t=${now}`, opts),
  ]);

  let staticData = null;
  let configData = null;

  if (staticRes.status === 'fulfilled' && staticRes.value.ok) {
    staticData = await staticRes.value.json();
  }

  if (configRes.status === 'fulfilled' && configRes.value.ok) {
    try { configData = await configRes.value.json(); } catch { /* ignore */ }
  }

  if (!staticData && !configData) return null;

  return {
    version: staticData?.version || null,
    forceUpdate: (staticData?.forceUpdate === true) || (configData?.forceUpdate === true),
    minVersion: maxVersion(staticData?.minVersion, configData?.minVersion),
    androidMinVersion: staticData?.androidMinVersion || configData?.androidMinVersion || null,
    message: configData?.message || null,
  };
}

/**
 * Hook para verificar periodicamente se há uma nova versão do app disponível.
 *
 * Fontes de dados (merge com critério mais restritivo):
 *   1. /version.json (estático, gerado no build)
 *   2. /version-config (Cloud Function, lê do Firestore — permite controle sem redeploy)
 *
 * Verifica:
 * - A cada 5 minutos (polling)
 * - Quando o usuário volta para a aba (visibilitychange)
 * - Quando reconecta após ficar offline (online)
 *
 * Suporta atualização forçada via:
 * - forceUpdate: true
 * - minVersion: "x.y.z" (força se versão atual < minVersion)
 *
 * @returns {Object} { updateAvailable, forceUpdate, newVersion, currentVersion, triggerUpdate, updateMessage }
 */
export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [updateMessage, setUpdateMessage] = useState(null);
  const [currentVersion] = useState(() => window.APP_VERSION || '');
  const intervalRef = useRef(null);
  const lastCheckRef = useRef(0);

  const checkVersion = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheckRef.current < 30000) return;
    lastCheckRef.current = now;

    if (isE2E() && window.__E2E_FORCE_VERSION__ !== true) return;
    if (String(currentVersion).indexOf('{{') === 0) return;

    try {
      const data = await fetchVersionData(now);
      if (!data) return;

      const { version: serverVersion, forceUpdate: serverForceUpdate, minVersion: serverMinVersion, message } = data;

      if (message) setUpdateMessage(message);

      const isBelowMinVersion = serverMinVersion && compareVersions(currentVersion, serverMinVersion) < 0;
      const shouldForce = !!serverForceUpdate || !!isBelowMinVersion;

      if (serverVersion && serverVersion !== currentVersion) {
        console.log('[VersionCheck] Nova versão disponível:', serverVersion, '(atual:', currentVersion, ')');
        setNewVersion(serverVersion);
        setUpdateAvailable(true);
        if (shouldForce) {
          console.warn('[VersionCheck] forceUpdate ativo — disparando atualização obrigatória');
          setForceUpdate(true);
        }
      } else if (!serverVersion) {
        if (shouldForce) {
          setUpdateAvailable(true);
          setForceUpdate(true);
        }
      } else if (serverVersion && serverVersion === currentVersion && shouldForce) {
        // Mesma versão mas server pede atualização (ex.: bundle corrompido em CDN).
        setUpdateAvailable(true);
        setForceUpdate(true);
      }
    } catch (err) {
      console.warn('[VersionCheck] Erro ao verificar versão:', err.message);
    }
  }, [currentVersion]);

  const triggerUpdate = useCallback(() => {
    window.__FORCE_UPDATE_IN_PROGRESS__ = true;

    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((reg) => reg.unregister());
      });
    }

    const url = new URL(window.location.href);
    url.searchParams.set('v', newVersion || Date.now().toString());
    window.location.replace(url.toString());
  }, [newVersion]);

  // Auto-reload obrigatório: quando o servidor sinaliza forceUpdate=true ou minVersion
  // acima da versão atual, recarregamos automaticamente apos 2s. O ForceUpdateModal
  // aparece nesse intervalo apenas como feedback visual — usuario nao precisa clicar.
  useEffect(() => {
    if (!forceUpdate) return;
    if (window.__FORCE_UPDATE_IN_PROGRESS__) return;
    // Em qualquer modo E2E não fazemos auto-reload: o ForceUpdateModal só é
    // exibido para inspeção (quando __E2E_FORCE_VERSION__ força o cenário).
    if (isE2E()) return;
    const timer = setTimeout(() => {
      console.warn('[VersionCheck] Auto-reload obrigatório iniciado');
      try { triggerUpdate(); } catch { /* fallback */ try { window.location.reload(); } catch {} }
    }, 2000);
    return () => clearTimeout(timer);
  }, [forceUpdate, triggerUpdate]);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      checkVersion();
    }, 10000);

    intervalRef.current = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    const handleOnline = () => {
      checkVersion();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkVersion]);

  return {
    updateAvailable,
    forceUpdate,
    newVersion,
    currentVersion,
    triggerUpdate,
    updateMessage,
  };
}

export default useVersionCheck;
